import axios from "axios";
import { useRouter } from "next/router";
import useSWR from "swr";
import { getUsedCarsApiRequestUrlForUrlPath } from "../../utils";
import { searchFilterKeys } from "../../constants/filter-constants";
import { useSearchStore } from "../use-search-store/use-search-store";
import { useShallow } from "zustand/react/shallow";
import { useEffect } from "react";
const httpClient = axios.create();
const fetcher = async (url)=>await httpClient.get(url, {
        headers: {
            "x-search-api-consumer": "used-cars-page-hook"
        }
    }).then((res)=>res.data);
const getUrlForPageNumber = (page, asPath)=>{
    const [urlPart, searchParamsPart] = asPath.split("?");
    const searchParams = new URLSearchParams(searchParamsPart);
    if (page === 1) {
        searchParams.delete("pageNumber");
    } else {
        searchParams.set("pageNumber", `${page}`);
    }
    if ([
        ...searchParams.values()
    ].length > 0) {
        return `${urlPart}?${searchParams}`;
    }
    return urlPart;
};
export function useUsedCarsData() {
    // NOTE: according the next.js useRouter docs, we shouldn't use `asPath` without checking `isReady`, and that can only be checked in a `useEffect`
    // Not doing so can cause issues when using SSR, with mismatches in the value between SSR and CSR
    // https://nextjs.org/docs/13/pages/api-reference/functions/use-router#router-object
    // But... looking at how it's used elsewhere in cinch-nx, no one is actually doing that (including us in the old useSearchFilters hook),
    // and no one has reported any issues, so we're going to use it as-is, without the `isReady` check.
    const { asPath  } = useRouter();
    const requestUrl = getUsedCarsApiRequestUrlForUrlPath(asPath);
    const searchParamMutators = useSearchStore(useShallow((state)=>({
            updateFilter: state.updateFilter,
            updateFilters: state.updateFilters,
            clearFilterValue: state.clearFilterValue,
            clearAllFilters: state.clearAllFilters,
            setPageNumber: state.setPageNumber,
            setSortingCriteria: state.setSortingCriteria,
            setUseMonthly: state.setUseMonthly
        })));
    const searchParams = useSearchStore(useShallow((state)=>({
            bodyType: state.bodyType,
            colour: state.colour,
            seats: state.seats,
            doors: state.doors,
            fromEngineSize: state.fromEngineSize,
            toEngineSize: state.toEngineSize,
            fuelType: state.fuelType,
            make: state.make,
            model: state.model,
            trim: state.trim,
            mileage: state.mileage,
            fromPrice: state.fromPrice,
            toPrice: state.toPrice,
            gearbox: state.gearbox,
            fromYear: state.fromYear,
            toYear: state.toYear,
            driveType: state.driveType,
            store: state.store,
            tags: state.tags,
            features: state.features,
            sortingCriteria: state.sortingCriteria,
            pageNumber: state.pageNumber,
            pageSize: state.pageSize,
            useMonthly: state.useMonthly
        })));
    const updateSearchParamsStateFromResponse = useSearchStore((state)=>state.updateSearchParamsStateFromResponse);
    const { isValidating , isLoading , data , error: usedCarsApiError  } = useSWR(requestUrl, fetcher, {
        keepPreviousData: true
    });
    // when we get data back from the server, update the store to keep it in sync
    useEffect(()=>{
        if (data) {
            updateSearchParamsStateFromResponse(data.parsedParamsFromUrl);
        }
    }, [
        data,
        updateSearchParamsStateFromResponse
    ]);
    // what should we do when we've not got data yet? (eventually we usually will always have it due to SWR fallback from SSR, I think)
    if (data) {
        // extract search results parts of response
        const searchResults = {
            pageNumber: data.pageNumber,
            pageSize: data.pageSize,
            searchResultsCount: data.searchResultsCount,
            vehicleListings: data.vehicleListings,
            facets: data.facets
        };
        const urlData = {
            hasMatchingVehicleRecentlyBeenInStock: data.hasMatchingVehicleRecentlyBeenInStock,
            pathSegmentsFromUrl: data.pathSegmentsFromUrl
        };
        const totalFilterCount = Object.entries(searchParams).reduce((currentCount, [paramKey, paramValue])=>{
            // only increment the count if it's a filter param, things like pageSize/useMonthly/sortingCriteria should not be included in this count
            if (!searchFilterKeys.includes(paramKey)) {
                return currentCount;
            }
            if (Array.isArray(paramValue)) {
                return currentCount + paramValue.length;
            }
            return currentCount + 1;
        }, 0);
        const pathSegmentFilters = Object.fromEntries(Object.entries(data.pathSegmentsFromUrl ?? {}).map(([key, value])=>[
                key,
                value.split(",")
            ]));
        return {
            // search results/API request props:
            isValidating,
            isLoading,
            usedCarsApiError,
            searchResults,
            urlData,
            // search filters/params props:
            searchParams,
            totalFilterCount,
            pathSegmentFilters,
            // search filters/params setters:
            ...searchParamMutators,
            // pagination:
            getUrlForPageNumber: (pageNumber)=>getUrlForPageNumber(pageNumber, asPath)
        };
    }
    return {
        // search results/API request props:
        isValidating,
        isLoading,
        usedCarsApiError,
        searchResults: undefined,
        urlData: undefined,
        // search filters/params props:
        searchParams,
        totalFilterCount: undefined,
        pathSegmentFilters: undefined,
        // search filters/params setters:
        ...searchParamMutators,
        // pagination:
        getUrlForPageNumber: (pageNumber)=>getUrlForPageNumber(pageNumber, asPath)
    };
}
